import PropTypes from 'prop-types';
import { MoonLoader } from 'react-spinners';
import {List, Set} from 'immutable';
import Device from './device.jsx';
import DateTime from 'react-datetime';


const LOG_LEVELS = {
  0: 'trace',
  1: 'debug',
  2: 'info',
  3: 'warn',
  4: 'error'
};

const componentColors = {
  app: '#009688',
  sdk: '#3F51B5',
  payworks: '#673AB7',
  ui: '#1abc9c',
  deprecatedSDK: '#795548',
  onoSDK: '#FF9800'
};

const moreColors = [
  '#4CAF50',
  '#009688',
  '#00BCD4',
  '#2196F3',
  '#3F51B5',
  '#9C27B0',
  '#E91E63',
  '#FFC107',
  '#607D8B',
  '#CDDC39',
  '#8BC34A',
  '#03A9F4',
  '#673AB7',
];

function componentColor(component) {
  if (componentColors[component]) {
    return componentColors[component];
  }

  const index = (Object.keys(componentColors).length - Object.keys(componentColors).length) % moreColors.length;
  componentColors[component] = moreColors[index];
  return componentColors[component];
}

class LogLine extends React.Component {
  prettyJSON(json) {
    // http://stackoverflow.com/questions/4810841/how-can-i-pretty-print-json-using-javascript
    const processedJSON = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return processedJSON.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, (match) => {
      let cls = 'number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'key';
        } else {
          cls = 'string';
        }
      } else if (/true|false/.test(match)) {
        cls = 'boolean';
      } else if (/null/.test(match)) {
        cls = 'null';
      }

      return `<span class='${cls}'>${match}</span>`;
    });
  }

  timestamp() {
    if (this.props.timestamp) {
      return moment(this.props.timestamp).format('YYYY/MM/DD hh:mm:ss');
    }

    return null;
  }

  logLevel() {
    return LOG_LEVELS[this.props.logLevel];
  }

  renderMetadata() {
    // If we have metadata and that metadata isn't in error
    if (this.props.metadata && !this.props.metadata.YocoJsonParseError && Object.keys(this.props.metadata).length > 0) {
      return (
        <a
          onClick={() => {
            waffle.utils.copyToClipboard(JSON.stringify(this.props.metadata, null, 2));
            console.log(this.props.metadata);
          }}

          title={`<pre class='json'>${this.prettyJSON(JSON.stringify(this.props.metadata, null, 2))}</pre>`}
          data-html='true'
          data-toggle='tooltip'
        >
          <i className='fa fa-paperclip fa-fw' />
        </a>
      );
    }

    // Keep things aligned
    return (
      <i className='fa fa-fw' />
    );
  }

  renderTAG() {
    if (this.props.metadata && !this.props.metadata.YocoJsonParseError && this.props.metadata.tag) {
      return (<div className={`line-tag`}>{ this.props.metadata.tag }</div>);
    }

    return null;
  }

  renderTransactionUUID() {
    if (this.props.metadata && !this.props.metadata.YocoJsonParseError && this.props.metadata.transactionUUID) {
      return (<div className={`line-transactionUUID`}>{ this.props.metadata.transactionUUID.substr(this.props.metadata.transactionUUID.length - 5) }</div>);
    }

    return null;
  }

  getComponent() {
    let component = this.props.component || "";
    while (component.length < this.props.componentMaximumLength) {
      component = component + '\u00a0';
    }

    return component;
  }

  render() {
    return (
      <div className={`log-line log-line-${this.logLevel()}`}>
        <div className='timestamp'>{ this.timestamp() }</div>
        <div className='component'>{ this.getComponent() }</div>
        <div className={`log-level log-level-${this.logLevel()}`}>{ this.logLevel() }</div>
        { this.renderMetadata() }
        { this.renderTransactionUUID() }
        { this.renderTAG() }
        <div className='message'>{ this.props.message }</div>
      </div>
    );
  }
}

LogLine.propTypes = {
  message: PropTypes.string.isRequired,
  component: PropTypes.string,
  timestamp: PropTypes.string,
  metadata: PropTypes.object,
  logLevel: PropTypes.number
};

export class LogDisplay extends React.Component {
  render() {
    return (
      <div className='log-text' key={this.props.key}>
        {
          (
            this.props.logLines
              .filter((logLine) => {
                if (logLine.message === undefined) {
                  // This is an unstructured log
                  return true;
                }

                if (!this.props.activeComponents.includes(logLine.component || "app")) {
                  return false;
                }

                return logLine.logLevel >= this.props.logLevel;
              })
              .map((logLine) => {
                if (logLine.message) {
                  return (
                    <LogLine
                      {...logLine}
                      componentMaximumLength={this.props.componentMaximumLength}
                    />
                  );
                }

                return <LogLine message={logLine} />;
              })
          )
        }
      </div>
    );
  }
}

LogDisplay.propTypes = {
};

LogDisplay.defaultProps = {
  showDevice: true
}


class LogWrapper extends waffle.base.BaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      activeComponents: Set(['app', 'ui']),
      logLevel: 2, // info
      components: List()
    };
  }

  toggleComponent(component) {
    if (this.state.activeComponents.includes(component)) {
      this.setState({activeComponents: this.state.activeComponents.delete(component)});
    } else {
      this.setState({activeComponents: this.state.activeComponents.add(component)});
    }
  }

  onLogLevelChange(event) {
    this.setState({logLevel: event.target.value});
  }

  renderHeader() {
    return null;
  }

  renderToolbar() {
    return (
      <div className='log-header'>
        <div className='log-actions'>
          <select
            value={this.state.logLevel.toString()}
            onChange={this.onLogLevelChange.bind(this)}
            className='log-level'
          >
            <option value='0'>Trace</option>
            <option value='1'>Debug</option>
            <option value='2'>Info</option>
            <option value='3'>Warn</option>
            <option value='4'>Error</option>
          </select>
          <div className='log-components'>
            {
              this.state.components.map((component) => {
                const componentClass = this.state.activeComponents.includes(component) ? 'active' : '';
                return (
                  <a
                    className={`toggle-component ${componentClass}`}
                    onClick={() => this.toggleComponent(component)}
                    style={{backgroundColor: componentColor(component)}}
                    key={component}
                  >
                    {component}
                  </a>
                );
              })
            }
          </div>
        </div>
        <div className='clearfix'/>
      </div>
    );
  }

  renderLogContents() {
    return (
      <div>No log contents</div>
    );
  }

  render() {
    let contents = null;

    if (this.state.errorMessage) {
      contents = (
        <div>{ this.state.errorMessage }</div>
      );
    } else if (!this.state.isFinishedLoading) {
      contents = (
        <MoonLoader size={25}/>
      );
    } else {
      contents = this.renderLogContents();
    }

    return (
      <div className='block'>
        { !!this.state.logLevel ? this.renderToolbar() : null }
        { this.renderHeader() }
        { contents }
      </div>
    );
  }
}

export class LogFile extends LogWrapper {
  constructor(props) {
    super();

    this.state = {};

    if (props.uuid) {
      this.state = {uuid: props.uuid};
    } else if (props.value) {
      // When passed from an InfoContentBlock
      this.state = {uuid: props.value};
    }
  }

  UNSAFE_componentWillMount() {
    this.fetchLogFile();
  }

  fetchLogFile(uuid) {
    radio('showLoader').broadcast();

    let api;
    if (this.state.uuid) {
      api = `/api/logs/${this.state.uuid}`;
    } else {
      const filters = {
        // We remove null values to avoid breaking the API by sending them as filters
        sourceUUID: this.props.sourceUUIDs.filter(sourceUUID => !!sourceUUID)
      }

      api = `/api/logs/?withLogContents=true&filters=${encodeURI(JSON.stringify(filters))}`;
    }

    $.ajax({
      url: api,
      dataType: 'json',
      type: 'GET'
    }).done((response) => {
      radio('hideLoader').broadcast();
      if (response.status === 200) {
        let componentSet = Set();

        // Sometimes we get multiple log files and its really confusing
        // displaying them separately but the log lines sorted within them

        let allLines = [];

        // Depending on the request we either get back a list of logFiles
        // or a single logFile as the data
        let logFiles = response.data.logFiles;
        if (!logFiles) {
          logFiles = [response.data];
        }

        logFiles.forEach((logFile) => {
          let logLines = logFile.logContents.split('\n')
              .filter(line => !!line)
              .map(line => JSON.parse(line));

          allLines = allLines.concat(logLines);
          componentSet = componentSet.union(
            Set(logLines.map(line => (line.component || "app")))
          );
        });

        const sortedLines = List(allLines).sortBy((logLine, index) => {
          return [moment(logLine.timestamp).unix(), index];
        });

        const sortedLogFiles = List(response.data.logFiles)
          .sortBy(logFile => logFile.logStart)
          .toJS();

        const components = componentSet.toList().sortBy(component => component.toLowerCase());
        const componentMaximumLength = components.map(com => com.length).sort().last()

        this.setState({
          errorCode: null,
          errorMessage: null,
          logLines: sortedLines.toJS(),
          logFiles: sortedLogFiles,
          components,
          componentMaximumLength,
          activeComponents: Set(this.props.defaultComponents),
          logLevel: this.props.defaultLogLevel,
          isFinishedLoading: true
        });
      } else {
        this.setState({
          errorCode: response.status,
          errorMessage: response.message
        });
      }
    }).fail((unused, textStatus) => {
      this.setState({
        errorMessage: textStatus
      });
    });
  }

  toggleComponent(component) {
    if (this.state.activeComponents.includes(component)) {
      this.setState({activeComponents: this.state.activeComponents.delete(component)});
    } else {
      this.setState({activeComponents: this.state.activeComponents.add(component)});
    }
  }

  onLogLevelChange(event) {
    this.setState({logLevel: event.target.value});
  }

  renderFileHeader() {
    if (this.state.logFile) {
      const lastModified = moment(this.state.logFile.lastUpdated).format('YYYY/MM/DD hh:mm:ss');

      return (
        <header>
          <h1>
            {this.state.logFile.logType} log file (last modified {lastModified})
          </h1>
        </header>
      );
    }

    return null;
  }


  renderHeader() {
    if (this.state.logFiles) {
      return (
        <div>
          {
            this.state.logFiles.map((logFile) => {
              return (
                <div>
                  Includes log file for source ({logFile.sourceUUID})
                  <Device {...logFile.metadata} />
                </div>
              )
            })
          }
        </div>
      )
    } else {
      return null;
    }
  }
  
  renderLogContents() {
    return (
      <div>
        <LogDisplay
          logLines={this.state.logLines}
          components={this.state.components}
          activeComponents={this.state.activeComponents}
          componentMaximumLength={this.state.componentMaximumLength}
          logLevel={this.state.logLevel}
        />
      </div>
    );
  }
}

LogFile.propTypes = {
  uuid: PropTypes.string, // Either a UUID or 'value' must be provided
  value: PropTypes.string, // `value` is for interop with Python instead of uuid
};

LogFile.defaultProps = {
  defaultComponents: Set(['app', 'ui']),
  defaultLogLevel: 2 // info
};

export class LogsInRange extends LogWrapper {
  componentWillMount() {
    this.setState({
      startDate: moment(this.props.startDate),
      endDate: moment(this.props.endDate)
    });
    setTimeout(() => {
      this.fetchLogsInRange(this.props.startDate, this.props.endDate, this.props.businessUUID);
    }, 0);
  }

  prettyDate(date) {
    return moment(date).format('YYYY/MM/DD HH:mm:ss');
  }

  hasDateChanged() {
    const startChanged = !moment(this.props.startDate).isSame(this.state.startDate);
    const endChanged = !moment(this.props.endDate).isSame(this.state.endDate);

    return startChanged || endChanged;
  }

  renderHeader() {
    const startDate = (
      <DateTime
        value={this.state.startDate}
        onChange={(newStartDate) => { this.setState({startDate: newStartDate})}}
      />
    );

    const endDate = (
      <DateTime
        value={this.state.endDate}
        onChange={(newEndDate) => { this.setState({endDate: newEndDate})}}
      />
    );

    const applyButton = this.hasDateChanged() && (
      <waffle.buttons.Button
        label='Apply'
        className='flat-button blue-background'
        onClick={this.fetchLogsInRange.bind(this)}
      />
    );

    return (
      <header>
        <h1>Logs between {startDate} and {endDate} {applyButton}</h1>
        <div className='actions'>
          <waffle.buttons.ApiButton
            label='Request detailed logs in range'
            className='flat-button blue-background'
            api={`/api/logs/requestLogsInRange?${this.logParams()}`}
          />
        </div>

      </header>
    );
  }

  logParams() {
    const filters = {
      businessUUID: encodeURIComponent(this.props.businessUUID),
      startDate: encodeURI(this.state.startDate.toISOString()),
      endDate: encodeURI(this.state.endDate.toISOString())
    }
    return `filters=${JSON.stringify(filters)}`;
  }

  fetchLogsInRange() {
    this.setState({
      errorCode: null,
      errorMessage: null,
      logFiles: List(),
      isFinishedLoading: false
    });

    radio('showLoader').broadcast();

    $.ajax({
      url: `/api/logs/?${this.logParams()}`,
      dataType: 'json',
      type: 'GET'
    }).done((response) => {
      radio('hideLoader').broadcast();
      if (response.status === 200) {
        let componentSet = Set();

        response.data.logFiles.forEach((logFile) => {
          let logLines = List(logFile.logContents.split('\n'));
          if (logFile.isStructured) {
            logLines = logLines.map(line => {
              try {
                return JSON.parse(line)
              } catch (e) {
                console.error("Log line contains invalid JSON", e);
                return {component: "app", message: "Unable to parse line: " + e, logLevel: 0, timestamp: '1970-01-01T00:00:00+02:00'}
              }
            });
            componentSet = componentSet.union(logLines.map(line => line.component).valueSeq().toList().toSet());
          }

          logFile.logLines = logLines;
        });

        const sortedLogFiles = List(response.data.logFiles).sortBy(logFile => logFile.logStart);

        const components = componentSet.toList().sortBy(component => component.toLowerCase());
        const componentMaximumLength = components.map(com => com.length).sort().last()

        this.setState({
          errorCode: null,
          errorMessage: null,
          logFiles: sortedLogFiles,
          components,
          componentMaximumLength,
          isFinishedLoading: true
        });
      } else {
        this.setState({
          errorCode: response.status,
          errorMessage: response.message
        });
      }
    }).fail((unused, textStatus) => {
      this.setState({
        errorMessage: textStatus
      });
    });
  }

  renderLogContents() {
    return (
      <div>
        {
          this.state.logFiles.map((logFile) => {
            return (
              <LogDisplay
                key={logFile.uuid}
                logFile={logFile}
                logLines={logFile.logLines}
                components={this.state.components}
                activeComponents={this.state.activeComponents}
                componentMaximumLength={this.state.componentMaximumLength}
                logLevel={this.state.logLevel}
              />
            );
          })
        }

      </div>
    );
  }
}

LogsInRange.defaultProps = {
  defaultComponents: Set(['app']),
  defaultLogLevel: 2 // info
};
